// components/PriceChart.js
import React, { useEffect, useRef } from "react";
import { createChart } from "lightweight-charts";

const PriceChart = ({ theme = "dark", chartData = [] }) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const resizeObserverRef = useRef(null);

  const ranges = {
    "1D": 1,
    "1W": 7,
    "1M": 30,
    "1Y": 365,
    All: 365,
  };

  useEffect(() => {
    if (!chartContainerRef.current || chartData.length === 0) return;

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 400,
      layout: {
        background: { color: "transparent" },
        textColor: theme === "dark" ? "#d1d4dc" : "#333333",
      },
      grid: {
        vertLines: { color: theme === "dark" ? "#2B2B43" : "#f0f3fa" },
        horzLines: { color: theme === "dark" ? "#2B2B43" : "#f0f3fa" },
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderVisible: false,
        fixLeftEdge: true,
        fixRightEdge: true,
      },
      crosshair: {
        mode: 1,
        vertLine: {
          color: theme === "dark" ? "#758696" : "#758696",
          labelBackground: theme === "dark" ? "#1e222d" : "#ffffff",
          labelVisible: true,
          timeVisible: true,
          labelFormatter: (time) => {
            const date = new Date(time * 1000);
            return date.toLocaleString(undefined, {
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            });
          },
        },
        horzLine: {
          color: theme === "dark" ? "#758696" : "#758696",
          labelBackground: theme === "dark" ? "#1e222d" : "#ffffff",
          labelVisible: true,
        },
      },
    });

    const candleSeries = chart.addCandlestickSeries({
      upColor: theme === "dark" ? "#26a69a" : "#26a69a",
      downColor: theme === "dark" ? "#ef5350" : "#ef5350",
      borderVisible: true,
      wickUpColor: theme === "dark" ? "#26a69a" : "#26a69a",
      wickDownColor: theme === "dark" ? "#ef5350" : "#ef5350",
      priceFormat: {
        type: "price",
        precision: 6,
        minMove: 0.000001,
      },
    });

    candleSeriesRef.current = candleSeries;
    chartRef.current = chart;

    candleSeries.setData(chartData);

    // Create and store the ResizeObserver
    resizeObserverRef.current = new ResizeObserver((entries) => {
      if (!chartContainerRef.current) return;
      chart.applyOptions({ width: chartContainerRef.current.clientWidth });
    });

    // Observe the chart container
    if (chartContainerRef.current) {
      resizeObserverRef.current.observe(chartContainerRef.current);
    }

    return () => {
      // Cleanup function
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      if (chartRef.current) {
        chartRef.current.remove();
      }
    };
  }, [chartData, theme]);

  return (
    <div
      ref={chartContainerRef}
      style={{
        width: "100%",
        height: "400px",
        backgroundColor: "transparent",
        marginBottom: "14px",
        position: "relative",
      }}
    ></div>
  );
};

export default PriceChart;
